import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useActiveSection from "@/hooks/useActiveSection";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lastScrollY = useRef(0);
  
  const sectionIds = ["how-it-works", "who-its-for", "about", "faq"];
  const activeSection = useActiveSection(sectionIds);

  // Close menu on resize (rotation fix)
  useEffect(() => {
    const handleResize = () => setIsOpen(false);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Close menu on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 20);
      if (isOpen && currentY < lastScrollY.current - 10) {
        setIsOpen(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isOpen]);

  const closeMenu = useCallback(() => setIsOpen(false), []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    closeMenu();
    
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const isActive = (href: string) => {
    const sectionId = href.replace("#", "");
    return activeSection === sectionId;
  };

  const navLinks = [
    { href: "#how-it-works", label: "How It Works" },
    { href: "#who-its-for", label: "Who It's For" },
    { href: "/blog", label: "Blog", isRoute: true },
    { href: "#about", label: "Our Story" },
  ];

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 transition-all duration-300 ${
        scrolled 
          ? "bg-cream/98 backdrop-blur-md border-b border-border shadow-sm" 
          : "bg-cream/95 backdrop-blur-sm border-b border-border/50"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <a 
          href="/" 
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
          className="flex items-center gap-2.5 group"
        >
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center group-hover:scale-105 transition-transform">
            <span className="text-primary-foreground font-serif font-bold text-lg">E</span>
          </div>
          <span className="font-serif text-xl font-semibold text-foreground tracking-tight">Erlara</span>
        </a>

        {/* Center Navigation - Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.isRoute ? (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.href);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors font-medium relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-coral hover:after:w-full after:transition-all"
              >
                {link.label}
              </a>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`relative font-medium transition-colors ${
                  isActive(link.href)
                    ? "text-coral"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-coral rounded-full"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </a>
            )
          ))}
        </div>

        {/* CTA - Desktop */}
        <div className="hidden md:flex items-center gap-6">
          <a 
            href="#faq" 
            onClick={(e) => handleNavClick(e, "#faq")}
            className="text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            FAQ
          </a>
          <a
            href="https://app.erlara.tech/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Log in
          </a>
          <a
            href="https://app.erlara.tech/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-all hover:shadow-lg group"
          >
            Get Started
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden p-2.5 text-foreground hover:bg-muted rounded-xl transition-colors"
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm md:hidden z-[-1]"
            onClick={closeMenu}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden"
          >
            <div className="max-w-7xl mx-auto pt-6 pb-8 space-y-2 bg-background/98 backdrop-blur-md rounded-b-2xl px-2">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      closeMenu();
                      navigate(link.href);
                    }}
                    className="block py-3 px-4 text-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl transition-all font-medium"
                  >
                    {link.label}
                  </a>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="block py-3 px-4 text-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl transition-all font-medium"
                  >
                    {link.label}
                  </a>
                )
              ))}
              <a
                href="#faq"
                onClick={(e) => handleNavClick(e, "#faq")}
                className="block py-3 px-4 text-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl transition-all font-medium"
              >
                FAQ
              </a>
              <a
                href="https://app.erlara.tech/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                className="block py-3 px-4 text-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl transition-all font-medium"
              >
                Log in
              </a>
              <div className="pt-4 px-4">
                <a
                  href="https://app.erlara.tech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-all"
                >
                  Get Started Free
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;
